import React from "react";
import { useTranslation } from "react-i18next";
import { DynamicTable } from "@maherunlocker/custom-react-table";
import { useSnapshot } from "valtio";
import { setMultiShopSwitch, store } from "@components/VerticalLayout/store";
import { CustomIconsComponent } from "./CustomIcons";
import PageTitle from "@components/Common/PageTitle";
import NotAuthorizationPage from "@components/VerticalLayout/NotAuthorizationPage";
import CardSpinnerWrapper from "@components/Common/CardSpinnerWrapper";
import AccountIdentifier from "./AccountIdentifier";
import {
    setEntityDetails,
    setFranchiseCountries,
} from "../../redux/globalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useFetchGlobal } from "../../api/hooks";
import { useAuthHeader } from "../../api/headersKeycloak";
import type { RootState } from "../../redux/store";
import {
    collectCountries,
    getCommercialName,
    uniqueNonEmpty,
} from "./treeHelpers";
import type {
    AccessResponse,
    CustomColumnProps,
    EntityListResponse,
} from "./types";

export function Compte() {
    const { t, i18n } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const { fetchGlobal } = useFetchGlobal();
    const { franchiseID } = useSnapshot(store);
    const dispatch = useDispatch();
    const [filterActive, setLocalFilterActive] = React.useState(false);
    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );
    const [isHaveAccess, setIsHaveAccess] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const entityDetails = useSelector(
        (state: RootState) => state.global.entityDetails
    );

    const uidEntity = localStorage.getItem("Uid_Entitie_User");
    const uuidUser = localStorage.getItem("uuidUser");

    const urlApiEntity = `${process.env.REACT_APP_ACCESS_API_URL}/entityv1/${uidEntity}/${uuidUser}`;
    const urlApi = `${process.env.REACT_APP_ACCESS_API_URL}/accounts/${uidEntity}/${uuidUser}`;

    React.useEffect(() => {
        const entityCached: EntityDetails = entityDetails;

        const fetchEntityData = async (): Promise<void> => {
            try {
                const response = await fetchGlobal<EntityListResponse>(
                    urlApiEntity,
                    {
                        method: "GET",
                        headers: { "Content-Type": "application/json" },
                    }
                );

                const entity = response.data?.[0];
                if (!entity) return;

                dispatch(setEntityDetails(entity));
                const countries = collectCountries(entity);
                dispatch(setFranchiseCountries(uniqueNonEmpty(countries)));

                localStorage.setItem("i18nextLng", i18n.language);

                setMultiShopSwitch(
                    franchiseID,
                    getCommercialName(entity, t("Commercial name"))
                );
            } catch (error) {
                console.error("Erreur API entity:", error);
            }
        };

        const isCachedTreeRoot =
            !!uidEntity && entityCached?.uid === uidEntity;
        if (!isCachedTreeRoot) {
            fetchEntityData();
        } else {
            try {
                setMultiShopSwitch(
                    franchiseID,
                    getCommercialName(entityCached, t("Commercial name"))
                );
            } catch (err) {
                console.log("There's an error ", err);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlApiEntity, franchiseID, i18n.language, t]);

    React.useEffect(() => {
        const accessCached: boolean | null = null;

        const fetchAccess = async (): Promise<void> => {
            try {
                const result = await fetchGlobal<AccessResponse>(urlApi, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });

                const hasAccess = (result.data?.length ?? 0) > 0;
                setIsHaveAccess(hasAccess);
            } catch (error) {
                console.error("Erreur API access:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (accessCached !== null) {
            setIsHaveAccess(accessCached);
            setIsLoading(false);
        } else {
            fetchAccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setDataIsUpdated(true);
    }, [i18n.language]);

    const arrayOfCustomColumns: CustomColumnProps[] = [
        {
            indexOFColumn: 8,
            columnName: t("Identifier"),
            customJsx: AccountIdentifier,
        },
        {
            indexOFColumn: 99,
            columnName: t("Actions"),
            customJsx: (e: any) => <CustomIconsComponent e={e} />,
        },
    ];
    return (
        <React.Fragment>
            <PageTitle title={t("Home")} />
            {!isLoading ? (
                !isHaveAccess ? (
                    <>
                        {" "}
                        compte
                        <NotAuthorizationPage />
                    </>
                ) : (
                    <div
                        className="page-content__clz"
                        style={{ width: "100%" }}
                    >
                        <h2 className="ml-5 mt-3">{t("Choose an account")}</h2>
                        <div
                            className="card"
                            style={{ padding: "20px", margin: "9px" }}
                        >
                            <DynamicTable
                                url={
                                    process.env.REACT_APP_VERIFY ===
                                        "localhost" ||
                                    process.env.REACT_APP_VERIFY === undefined
                                        ? "./user.json"
                                        : urlApiEntity
                                }
                                requestHeader={{
                                    "Accept-Language": i18n.language,
                                    ...getAuthHeader,
                                }}
                                canExpand={true}
                                setDataIsUpdated={setDataIsUpdated}
                                dataIsUpdated={dataIsUpdated}
                                showFilter
                                canSelect={false}
                                setLocalFilterActive={setLocalFilterActive}
                                filterActive={filterActive}
                                actionColumn={() => <></>}
                                arrayOfCustomColumns={arrayOfCustomColumns}
                                canResize
                                showGlobalFilter
                                elevationTable={1}
                                name="Choice"
                                minHeight={"500px"}
                                maxHeight={"500px"}
                            />
                        </div>
                    </div>
                )
            ) : (
                <CardSpinnerWrapper />
            )}
        </React.Fragment>
    );
}
